import React, { useState, useRef, useEffect } from "react";
import { stateData, panelTypes, roofTypes, financeOpts, shadingOpts, dirOpts, creditOpts, PANEL_AREA_SQFT, ITC_RATE, BATTERY_COST_PER_KWH } from "../shared/Data";
import { round, parseNumber } from "../shared/helpers";
import "../style.css";

// Estimates based on US EIA 2024 data, NREL PVWatts methodology, and the 30% federal ITC.

const DEFAULTS = {
  basePerformanceRatio: 0.82,        
  degradationRate: 0.005,            
  inverterReserve: 0.015,           
  maintenanceRate: 0.01,             
  batteryCostPerKwh: BATTERY_COST_PER_KWH,
  batteryPackageFactor: 1.15,         
  leaseSavingsFactor: 0.55,           
  leasePaymentShare: 0.60,            
  loanTermYears: 15,
  loanFeeFactor: 0.01,                
  paybackCapYears: 25,
};

const ROOF_COMPLEXITY_FACTOR = {
  asphalt: 1.00,
  tile: 1.06,
  metal: 1.04,
  flat: 1.08,
};

const CREDIT_RATE_LOOKUP = {
  excellent: 0.045,
  good: 0.055,
  fair: 0.075,
  low: 0.095,
};

const SOLAR_ENVIRONMENT = {
  emissionsPerKwh: 0.92,             
  treeEquivalentLbs: 48,              
};

function calculateUsage(form, tariff) {
  const monthlyBill = Math.max(0, parseFloat(form.monthlyBill) || 0);
  const baseMonthlyKwh = tariff > 0 ? monthlyBill / tariff : 0;
  const evMonthlyKwh = form.ev === "yes" ? 200 : 0; 
  const annualKwh = (baseMonthlyKwh + evMonthlyKwh) * 12;

  return {
    monthlyKwh: round(baseMonthlyKwh + evMonthlyKwh, 1),
    annualKwh: round(annualKwh, 0),
    evMonthlyKwh,
  };
}

function calculateSystemSize(annualKwh, sd, shadingFactor, dirFactor) {
  const weatherProduction = sd.sun * 12; 
  const systemPerformance = DEFAULTS.basePerformanceRatio * shadingFactor * dirFactor * 0.96 * 0.97 * 0.98; 
  const annualProductionPerKw = weatherProduction * systemPerformance;
  const requiredKw = annualProductionPerKw > 0 ? annualKwh / annualProductionPerKw : 0;

  return {
    annualProductionPerKw,
    performanceRatio: systemPerformance,
    requiredKw: Math.max(0, requiredKw),
  };
}

function calculateRoofCapacity(roofArea, panel, roofType, requiredPanels) {
  const panelsFit = roofArea > 0 ? Math.floor(roofArea / PANEL_AREA_SQFT) : 0;
  const fitPercent = panelsFit > 0 ? Math.min(100, (requiredPanels / panelsFit) * 100) : 0;
  const complexityFactor = ROOF_COMPLEXITY_FACTOR[roofType] || 1.0;

  return {
    panelsFit,
    roofUtilizationPct: round(Math.min(100, (requiredPanels <= panelsFit ? requiredPanels : panelsFit) / Math.max(1, panelsFit) * 100), 0),
    roofComplexityFactor: complexityFactor,
    panelsNeeded: requiredPanels,
  };
}

function calculateProduction(actualKw, sd, performanceRatio) {
  const annualProd = actualKw * sd.sun * 12 * performanceRatio;
  return {
    annualProd: Math.max(0, annualProd),
    monthlyProd: annualProd / 12,
  };
}

function calculateBattery(form, annualKwh, hasEv) {
  if (form.battery !== "yes") {
    return { batterySizeKwh: 0, batteryCost: 0, batteryNotes: "No battery selected" };
  }

  const dailyUsage = annualKwh / 365;
  const desiredBackupHours = hasEv ? 6 : 4;
  const batterySizeKwh = Math.min(20, Math.max(8, dailyUsage * desiredBackupHours * 0.5));
  const batteryCost = batterySizeKwh * DEFAULTS.batteryCostPerKwh * DEFAULTS.batteryPackageFactor;

  return {
    batterySizeKwh: round(batterySizeKwh, 1),
    batteryCost: round(batteryCost, 0),
    batteryNotes: `${round(batterySizeKwh, 1)} kWh backup capacity`,
  };
}

function calculateFinancials(inputs) {
  const {
    netCost,
    annualSavings,
    annualKwh,
    tariff,
    annualProduction,
    sd,
    form,
    inverterReserve,
    maintenanceReserve,
  } = inputs;

  const baselineAnnualBill = annualKwh * tariff;
  const financeType = form.finance || "cash";
  const leaseSavings = financeType === "lease" ? annualProduction * tariff * DEFAULTS.leaseSavingsFactor * sd.netMeteringFactor : annualSavings;
  const annualCashSavings = financeType === "lease" ? leaseSavings : annualSavings;
  const monthlySavings = annualCashSavings / 12;
  const paybackYears = annualCashSavings > 0 ? netCost / annualCashSavings : Infinity;
  const roi = netCost > 0 ? (annualCashSavings * DEFAULTS.paybackCapYears - inverterReserve - maintenanceReserve) / netCost : 0;

  const financing = {
    type: financeType,
    monthlyPayment: 0,
    totalPayment: 0,
    totalInterest: 0,
    loanTermYears: 0,
    annualSavings: round(annualCashSavings, 0),
    monthlySavings: round(monthlySavings, 0),
    paybackYears: paybackYears > DEFAULTS.paybackCapYears ? `>${DEFAULTS.paybackCapYears}` : round(paybackYears, 1),
    roi: round(roi * 100, 1),
    notes: "",
  };

  if (financeType === "loan") {
    const creditRate = CREDIT_RATE_LOOKUP[form.credit] || 0.065;
    const financedAmount = netCost * (1 + DEFAULTS.loanFeeFactor);
    const monthlyRate = creditRate / 12;
    const n = DEFAULTS.loanTermYears * 12;
    const payment = financedAmount > 0 ? financedAmount * (monthlyRate / (1 - Math.pow(1 + monthlyRate, -n))) : 0;
    financing.monthlyPayment = round(payment, 2);
    financing.totalPayment = round(payment * n, 0);
    financing.totalInterest = round(financing.totalPayment - financedAmount, 0);
    financing.loanTermYears = DEFAULTS.loanTermYears;
    financing.notes = `Solar loan at ${round(creditRate * 100, 2)}% APR for ${DEFAULTS.loanTermYears} years`;
  }

  if (financeType === "lease") {
    financing.monthlyPayment = round((baselineAnnualBill * DEFAULTS.leasePaymentShare) / 12, 0);
    financing.totalPayment = round(financing.monthlyPayment * 12 * DEFAULTS.paybackCapYears, 0);
    financing.notes = "Lease/PPA assumes lower ownership savings and no federal ITC credit";
  }

  if (financeType === "cash") {
    financing.notes = "Cash purchase maximizes long-term savings and ownership benefits.";
  }

  return financing;
}

function calculateEnvironmentalImpact(annualProduction, tariff, sd) {
  const years = 25;
  const degradation = DEFAULTS.degradationRate;
  const inflation = sd.inflation || 0.03;
  let lifetimeSavingsValue = 0;
  let lifetimeProduction = 0;

  for (let year = 1; year <= years; year += 1) {
    const production = annualProduction * Math.pow(1 - degradation, year - 1);
    const value = production * tariff * Math.pow(1 + inflation, year - 1) * sd.netMeteringFactor;
    lifetimeProduction += production;
    lifetimeSavingsValue += value;
  }

  const co2Offset = lifetimeProduction * SOLAR_ENVIRONMENT.emissionsPerKwh / years;
  const treesEq = co2Offset > 0 ? round(co2Offset / SOLAR_ENVIRONMENT.treeEquivalentLbs, 0) : 0;

  return {
    annualCo2Offset: round(annualProduction * SOLAR_ENVIRONMENT.emissionsPerKwh, 0),
    treesEq,
    lifetimeSavings: round(lifetimeSavingsValue, 0),
    lifetimeProduction: round(lifetimeProduction, 0),
  };
}

function OptBtn({ icon, label, sub, selected, onClick }) {
  return (
    <div className={`se-opt-btn ${selected ? "se-opt-btn-sel" : ""}`} onClick={onClick}>
      <div className="se-opt-icon"><i className={`ti ${icon}`} /></div>
      <div className="se-opt-label">{label}</div>
      {sub && <div className="se-opt-sub">{sub}</div>}
    </div>
  );
}

function ToggleItem({ icon, label, selected, onClick }) {
  return (
    <div className={`se-toggle-item ${selected ? "se-toggle-sel" : ""}`} onClick={onClick}>
      <i className={`ti ${icon}`} />
      <span>{label}</span>
    </div>
  );
}

function ResCard({ label, value, sub }) {
  return (
    <div className="se-res-card">
      <div className="se-res-label">{label}</div>
      <div className="se-res-val">{value}</div>
      {sub && <div className="se-res-sub">{sub}</div>}
    </div>
  );
}

function IncentiveRow({ label, value, green, isTotal }) {
  return (
    <div className={`se-incentive-row ${isTotal ? "se-incentive-row-total" : ""}`}>
      <span className={isTotal ? "se-incentive-total-key" : "se-incentive-key"}>{label}</span>
      <span className={isTotal ? "se-incentive-total-val" : green ? "se-incentive-green" : "se-incentive-val"}>{value}</span>
    </div>
  );
}

function calcResults(form) {
  const sd = stateData[form.selectedState] || stateData["Texas"];
  const tariff = parseNumber(form.tariff, sd.utility);
  const roofArea = Math.max(0, parseNumber(form.roofArea));
  const panel = panelTypes.find(p => p.id === form.panelType) || panelTypes[0];
  const shadingFactor = (shadingOpts.find(s => s.id === form.shading) || shadingOpts[0]).factor;
  const dirFactor = (dirOpts.find(d => d.id === form.roofDir) || dirOpts[0]).factor;
  const roofType = form.roofType || "asphalt";

  const usage = calculateUsage(form, tariff);
  const systemSize = calculateSystemSize(usage.annualKwh, sd, shadingFactor, dirFactor);
  const moduleWatt = Math.max(0, parseNumber(form.moduleWatt, panel.defaultWatt));
  const panelsNeeded = moduleWatt > 0 ? Math.ceil((systemSize.requiredKw * 1000) / moduleWatt) : 0;
  const roofCapacity = calculateRoofCapacity(roofArea, panel, roofType, panelsNeeded);

  const actualPanels = Math.min(panelsNeeded, roofCapacity.panelsFit);
  const actualKw = round((actualPanels * moduleWatt) / 1000, 2);
  const actualProduction = calculateProduction(actualKw, sd, systemSize.performanceRatio);

  const battery = calculateBattery(form, usage.annualKwh, form.ev === "yes");
  const systemCostBase = actualKw * 1000 * sd.installCostPerWatt * roofCapacity.roofComplexityFactor;
  const systemCost = round(systemCostBase, 0);
  const federalItc = form.taxLiability === "yes" && form.finance !== "lease" ? round(systemCost * ITC_RATE, 0) : 0;
  const stateIncentive = round(systemCost * (sd.incentiveRate || 0.03), 0);
  const netCost = round(systemCost + battery.batteryCost - federalItc - stateIncentive, 0);

  const annualProductionValue = round(actualProduction.annualProd * tariff * sd.netMeteringFactor, 0);
  const annualSavings = form.finance === "lease"
    ? round(actualProduction.annualProd * tariff * sd.netMeteringFactor * DEFAULTS.leaseSavingsFactor, 0)
    : annualProductionValue;

  const roofWarning = panelsNeeded > roofCapacity.panelsFit;
  const inverterReserve = round(systemCost * DEFAULTS.inverterReserve, 0);
  const maintenanceReserve = round(systemCost * DEFAULTS.maintenanceRate * DEFAULTS.paybackCapYears, 0);

  const financials = calculateFinancials({
    netCost,
    annualSavings,
    annualKwh: usage.annualKwh,
    tariff,
    annualProduction: actualProduction.annualProd,
    sd,
    form,
    inverterReserve,
    maintenanceReserve,
  });

  const environmental = calculateEnvironmentalImpact(actualProduction.annualProd, tariff, sd);
  const coverPct = usage.annualKwh > 0 ? Math.round(Math.min(100, (actualProduction.annualProd / usage.annualKwh) * 100)) : 0;

  return {
    monthlyKwh: round(usage.monthlyKwh, 0),
    annualKwh: round(usage.annualKwh, 0),
    requiredKw: round(systemSize.requiredKw, 2),
    performanceRatio: round(systemSize.performanceRatio, 3),
    moduleWatt,
    requiredPanels: panelsNeeded,
    roofWarning,
    finalKW: actualKw,
    finalPanels: actualPanels,
    panelsFit: roofCapacity.panelsFit,
    panelsNeeded,
    annualProd: round(actualProduction.annualProd, 0),
    systemCost,
    batteryCost: battery.batteryCost,
    batterySizeKwh: battery.batterySizeKwh,
    batteryNotes: battery.batteryNotes,
    itcCredit: federalItc,
    stateIncentive,
    netCost,
    annualSavings,
    monthlySavings: round(annualSavings / 12, 0),
    payback: financials.paybackYears,
    loanMonthlyPayment: financials.monthlyPayment,
    loanTotalPayment: financials.totalPayment,
    loanTotalInterest: financials.totalInterest,
    loanTermYears: financials.loanTermYears,
    financeNotes: financials.notes,
    roi: financials.roi,
    inverterReserve,
    maintenanceReserve,
    lifetimeSavings: environmental.lifetimeSavings,
    lifetime25: round(environmental.lifetimeSavings - inverterReserve - maintenanceReserve, 0),
    co2Offset: environmental.annualCo2Offset,
    treesEq: environmental.treesEq,
    coverPct,
    roofUtilization: roofCapacity.roofUtilizationPct,
    utilityInflation: sd.inflation,
    incentiveBreakdown: {
      federalItc,
      stateIncentive,
      totalIncentives: federalItc + stateIncentive,
    },
  };
}

const STEPS = [
  { id: 1, label: "Location" },
  { id: 2, label: "Your home" },
  { id: 3, label: "Energy" },
  { id: 4, label: "Preferences" },
  { id: 5, label: "Financing" },
  { id: 6, label: "Results" },
];

function ProgressBar({ step }) {
  return (
    <div className="se-progress-bar">
      {STEPS.map((s, i) => {
        const done   = s.id < step;
        const active = s.id === step;
        return (
          <div key={s.id} className="se-step-container">
            <div className="se-step-dot">
              <div className={`se-dot-circle-base ${done ? "se-dot-done" : active ? "se-dot-active" : "se-dot-future"}`}>
                {done ? <i className="ti ti-check" /> : s.id}
              </div>
              <div className={`se-dot-label ${active ? "se-dot-label-active" : ""}`}>{s.label}</div>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`se-step-line ${done ? "se-step-line-done" : ""}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Step1({ form, updateForm, error }) {
  const sd = form.selectedState ? stateData[form.selectedState] : null;
  return (
    <div className="se-card">
      <div className="se-card-header">
        <div className="se-card-icon"><i className="ti ti-map-pin" /></div>
        <div>
          <div className="se-card-title">Where is your home located?</div>
          <div className="se-card-sub">Solar output varies significantly by state.</div>
        </div>
      </div>
      <div className="se-field">
        <label className="se-label">Select your state</label>
        <select
          className="se-select"
          value={form.selectedState}
          onChange={e => {
            const stateVal = e.target.value;
            const utility = stateData[stateVal]?.utility.toFixed(3) || "";
            updateForm({ selectedState: stateVal, tariff: utility });
          }}
        >
          <option value="">— Choose a state —</option>
          {Object.keys(stateData).sort().map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      {sd && (
        <div className="se-badge-row">
          <span className="se-badge-purple">☀ {sd.sun} avg monthly kWh/kW</span>
          <span className="se-badge-amber">Avg rate: ${sd.utility.toFixed(3)}/kWh</span>
        </div>
      )}
      {error && <div className="se-err">{error}</div>}
    </div>
  );
}

function Step2({ form, updateForm, error }) {
  return (
    <div className="se-card">
      <div className="se-card-header">
        <div className="se-card-icon"><i className="ti ti-home-2" /></div>
        <div>
          <div className="se-card-title">Tell us about your home</div>
          <div className="se-card-sub">Roof size and condition determine how many panels can be installed.</div>
        </div>
      </div>
      <div className="se-field-grid2">
        <div className="se-field">
          <label className="se-label">Home size (sq ft)</label>
          <input type="number" className="se-input" placeholder="e.g. 2000"
            value={form.homeSize} onChange={e => updateForm({ homeSize: e.target.value })} />
          <span className="se-hint">Average US home: ~2,300 sq ft</span>
        </div>
        <div className="se-field">
          <label className="se-label">Usable roof area (sq ft)</label>
          <input type="number" className="se-input" placeholder="e.g. 800"
            value={form.roofArea} onChange={e => updateForm({ roofArea: e.target.value })} />
          <span className="se-hint">Typically 40–60% of home size</span>
        </div>
        <div className="se-field">
          <label className="se-label">Roof age</label>
          <select className="se-select" value={form.roofAge} onChange={e => updateForm({ roofAge: e.target.value })}>
            <option value="">Select</option>
            <option value="0-5">0–5 years (excellent)</option>
            <option value="6-10">6–10 years (good)</option>
            <option value="11-20">11–20 years (fair)</option>
            <option value="21+">21+ years (may need repair)</option>
          </select>
        </div>
        <div className="se-field">
          <label className="se-label">Primary roof direction</label>
          <select className="se-select" value={form.roofDir} onChange={e => updateForm({ roofDir: e.target.value })}>
            <option value="">Select</option>
            {dirOpts.map(d => <option key={d.id} value={d.id}>{d.label} {d.sub ? `(${d.sub})` : ""}</option>)}
          </select>
        </div>
      </div>
      <div className="se-section-label">Roof type</div>
      <div className="se-opt-grid">
        {roofTypes.map(r => (
          <OptBtn key={r.id} icon="ti-home-2" label={r.label} sub={r.sub}
            selected={form.roofType === r.id}
            onClick={() => updateForm({ roofType: r.id })} />
        ))}
      </div>
      {error && <div className="se-err">{error}</div>}
    </div>
  );
}

function Step3({ form, updateForm, error }) {
  return (
    <div className="se-card">
      <div className="se-card-header">
        <div className="se-card-icon"><i className="ti ti-bolt" /></div>
        <div>
          <div className="se-card-title">Your energy usage</div>
          <div className="se-card-sub">We'll size your system to cover your actual consumption.</div>
        </div>
      </div>
      <div className="se-field-grid2">
        <div className="se-field">
          <label className="se-label">Average monthly bill ($)</label>
          <input type="number" className="se-input" placeholder="e.g. 180"
            value={form.monthlyBill} onChange={e => updateForm({ monthlyBill: e.target.value })} />
          <span className="se-hint">Check your last 3 utility bills</span>
        </div>
        <div className="se-field">
          <label className="se-label">Electricity rate ($/kWh)</label>
          <input type="number" className="se-input" placeholder="e.g. 0.14" step="0.001"
            value={form.tariff} onChange={e => updateForm({ tariff: e.target.value })} />
          <span className="se-hint">Pre-filled from your state avg</span>
        </div>
      </div>
      <div className="se-section-label">Shading on your roof</div>
      <div className="se-opt-grid">
        {shadingOpts.map(s => (
          <OptBtn key={s.id} icon="ti-sun" label={s.label} sub={s.sub}
            selected={form.shading === s.id}
            onClick={() => updateForm({ shading: s.id })} />
        ))}
      </div>
      <div className="se-section-label">Do you have or plan to get an electric vehicle?</div>
      <div className="se-toggle-grid">
        <ToggleItem icon="ti-car" label="Yes — add EV load" selected={form.ev === "yes"} onClick={() => updateForm({ ev: "yes" })} />
        <ToggleItem icon="ti-x" label="No EV planned"       selected={form.ev === "no"}  onClick={() => updateForm({ ev: "no" })} />
      </div>
      {error && <div className="se-err">{error}</div>}
    </div>
  );
}

function Step4({ form, updateForm, error }) {
  return (
    <div className="se-card">
      <div className="se-card-header">
        <div className="se-card-icon"><i className="ti ti-sun-wind" /></div>
        <div>
          <div className="se-card-title">Solar preferences</div>
          <div className="se-card-sub">Panel type and add-ons that match your goals.</div>
        </div>
      </div>
      <div className="se-section-label">Panel technology</div>
      <div className="se-opt-grid">
        {panelTypes.map(p => (
          <OptBtn key={p.id} icon="ti-sun" label={p.label} sub={p.sub}
            selected={form.panelType === p.id}
            onClick={() => updateForm({ panelType: p.id, moduleWatt: p.defaultWatt })} />
        ))}
      </div>
      <div className="se-field" style={{ marginTop: 12 }}>
        <label className="se-label">Desired module wattage (W)</label>
        <input type="number" min="250" max="800" inputMode="numeric" className="se-input" value={form.moduleWatt} placeholder="Example: 400" onChange={e => updateForm({ moduleWatt: e.target.value })} />
        <span className="se-hint">Auto-filled from panel type, but you can override if needed.</span>
      </div>
      <div className="se-section-label">Battery storage (backup power)</div>
      <div className="se-opt-grid">
        <OptBtn icon="ti-battery-charging-2" label="Yes, add battery" sub="Backup + savings"
          selected={form.battery === "yes"} onClick={() => updateForm({ battery: "yes" })} />
        <OptBtn icon="ti-battery" label="Solar only" sub="No battery"
          selected={form.battery === "no"}  onClick={() => updateForm({ battery: "no" })} />
      </div>
      {/* <div className="se-section-label">Monitoring preference</div>
      <div className="se-toggle-grid">
        <ToggleItem icon="ti-device-mobile" label="App-based monitoring" selected={form.monitoring === "app"} onClick={() => updateForm({ monitoring: "app" })} />
        <ToggleItem icon="ti-device-laptop" label="Web portal"           selected={form.monitoring === "portal"} onClick={() => updateForm({ monitoring: "portal" })} />
      </div> */}
      {error && <div className="se-err">{error}</div>}
    </div>
  );
}

function Step5({ form, updateForm, error }) {
  return (
    <div className="se-card">
      <div className="se-card-header">
        <div className="se-card-icon"><i className="ti ti-coin" /></div>
        <div>
          <div className="se-card-title">Financing & incentives</div>
          <div className="se-card-sub">How you pay affects your total savings and payback period.</div>
        </div>
      </div>
      <div className="se-section-label">How do you plan to go solar?</div>
      <div className="se-opt-grid">
        {financeOpts.map(f => (
          <OptBtn key={f.id} icon="ti-coin" label={f.label} sub={f.sub}
            selected={form.finance === f.id}
            onClick={() => updateForm({ finance: f.id })} />
        ))}
      </div>
      <div className="se-section-label">Credit score range</div>
      <div className="se-opt-grid">
        {creditOpts.map(c => (
          <OptBtn key={c.id} icon="ti-star" label={c.label} sub={c.sub}
            selected={form.credit === c.id}
            onClick={() => updateForm({ credit: c.id })} />
        ))}
      </div>
      <div className="se-section-label">Federal tax liability (for 30% ITC credit)</div>
      <div className="se-toggle-grid">
        <ToggleItem icon="ti-receipt-tax" label="I have federal tax liability" selected={form.taxLiability === "yes"} onClick={() => updateForm({ taxLiability: "yes" })} />
        <ToggleItem icon="ti-x"           label="I do not owe federal taxes"   selected={form.taxLiability === "no"}  onClick={() => updateForm({ taxLiability: "no" })} />
      </div>
      {error && <div className="se-err">{error}</div>}
    </div>
  );
}

function Step6({ form, resultsRef }) {
  const r = calcResults(form);
  const panel = panelTypes.find(p => p.id === form.panelType) || panelTypes[0];

  return (
    <div ref={resultsRef}>
      {/* Highlight */}
      <div className="se-highlight-card">
        <div>
          <div className="se-big-num">${r.monthlySavings.toLocaleString()}<span>/mo</span></div>
          <div className="se-big-num-sub">Estimated monthly savings</div>
        </div>
        <div className="se-highlight-info">
          <div className="se-highlight-label">Energy covered by solar</div>
          <div className="se-savings-bar">
            <div className="se-savings-fill" style={{ width: `${r.coverPct}%` }} />
          </div>
          <div className="se-highlight-sub-label">{r.coverPct}% of your usage offset</div>
        </div>
      </div> 

      {/* System breakdown */}
      <div className="se-card">
        <div className="se-card-header">
          <div className="se-card-icon"><i className="ti ti-solar-panel" /></div>
          <div><div className="se-card-title">System breakdown</div></div>
        </div>
        <div className="se-results-grid">
          <ResCard label="Module wattage" value={`${r.moduleWatt} W`} sub={`${panel.label}`} />
          <ResCard label="System size"       value={`${r.finalKW} kW`}          sub={`${r.finalPanels} panels installed`} />
          <ResCard label="Annual production" value={r.annualProd.toLocaleString()} sub="kWh per year" />
          <ResCard label="Payback period"    value={`${r.payback} yrs`}          sub={`${(stateData[form.selectedState]?.sun || stateData["Texas"].sun)} avg sun hrs/mo`} />
          <ResCard label="25-yr net savings" value={`$${r.lifetime25.toLocaleString()}`} sub="After all costs" />
        </div>
      </div>

      {/* Cost & incentives */}
      <div className="se-card">
        <div className="se-card-header">
          <div className="se-card-icon"><i className="ti ti-coin" /></div>
          <div><div className="se-card-title">Cost & incentives</div></div>
        </div>
        <IncentiveRow label="Panel system cost"          value={`$${r.systemCost.toLocaleString()}`} />
        {r.batteryCost > 0 && <IncentiveRow label="Battery storage (10 kWh)" value={`$${r.batteryCost.toLocaleString()}`} />}
        <IncentiveRow label="Federal ITC (30%)"          value={`− $${r.itcCredit.toLocaleString()}`}       green />
        <IncentiveRow label="State incentive (est.)"     value={`− $${r.stateIncentive.toLocaleString()}`}  green />
        <IncentiveRow label="Net cost after incentives"  value={`$${r.netCost.toLocaleString()}`}  isTotal />
      </div>

      {/* Environmental */}
      <div className="se-card">
        <div className="se-card-header">
          <div className="se-card-icon"><i className="ti ti-leaf" /></div>
          <div><div className="se-card-title">Environmental impact</div></div>
        </div>
        <div className="se-results-grid">
          <ResCard label="CO₂ offset / year" value={r.co2Offset.toLocaleString()} sub="lbs of CO₂ avoided" />
          <ResCard label="Equivalent trees"  value={r.treesEq}                     sub="planted per year" />
        </div>
        <div className="se-badge-row">
          <span className="se-badge-green">Carbon neutral energy</span>
          <span className="se-badge-purple">{form.selectedState}</span>
          <span className="se-badge-amber">{panel.label}</span>
          {form.battery === "yes" && <span className="se-badge-green">Battery backup</span>}
        </div>
      </div>

      {r.roofWarning && (
        <div className="se-err" style={{ marginTop: 16 }}>
          Roof area may not support the recommended system size.
        </div>
      )}
      <div className="se-disclaimer">
        Estimates based on US EIA 2024 data, NREL PVWatts methodology, and the 30% federal ITC.<br />
        Actual results may vary. Consult a certified solar installer for a site-specific quote.
      </div>
    </div>
  );
}

const defaultForm = {
  selectedState: "",
  homeSize: "",
  roofArea: "",
  roofAge: "",
  roofDir: "",
  roofType: "",
  monthlyBill: "",
  tariff: "",
  shading: "none",
  ev: "no",
  panelType: "mono",
  moduleWatt: 400,
  battery: "no",
  monitoring: "app",
  finance: "cash",
  credit: "",
  taxLiability: "yes",
};

export default function SolarCalculator() {
  const [step, setStep]   = useState(1);
  const [form, setForm]   = useState(defaultForm);
  const [error, setError] = useState("");
  const resultsRef = useRef(null);

  useEffect(() => {
    if (step === 6) {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  const updateForm = (fields) => {
    setForm(prev => ({ ...prev, ...fields }));
  };

  function validate() {
    if (step === 1 && !form.selectedState)                           { setError("Please select your state to continue."); return false; }
    if (step === 2 && (!form.roofArea || parseFloat(form.roofArea) < 50)) { setError("Please enter a valid roof area (min 50 sq ft)."); return false; }
    if (step === 3 && (!form.monthlyBill || parseFloat(form.monthlyBill) < 5)) { setError("Please enter your average monthly electricity bill."); return false; }
    if (step === 4) {
      const moduleWattValue = parseFloat(form.moduleWatt);
      if (!Number.isFinite(moduleWattValue) || moduleWattValue < 250 || moduleWattValue > 800) {
        setError("Module wattage must be a number between 250 and 800."); return false;
      }
    }
    return true;
  }

  function goNext() {
    if (!validate()) return;
    setError("");
    setStep(s => Math.min(s + 1, 6));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goBack() {
    setError("");
    setStep(s => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const stepProps = { form, updateForm, error };

  return (
    <div className="se-wrap">
      {/* Hero */}
      {/* <div className="se-hero">
        <div className="se-hero-badge">Solar Estimator</div>
        <h1 className="se-hero-h1">How much could solar<br />save you?</h1>
        <p className="se-hero-p">Answer 6 quick steps — get a personalized estimate in under 2 minutes.</p>
      </div> */}

      <ProgressBar step={step} /> 

      {step === 1 && <Step1 {...stepProps} />}
      {step === 2 && <Step2 {...stepProps} />}
      {step === 3 && <Step3 {...stepProps} />}
      {step === 4 && <Step4 {...stepProps} />}
      {step === 5 && <Step5 {...stepProps} />}
      {step === 6 && <Step6 form={form} resultsRef={resultsRef} />}

      {/* Navigation */}
      <div className="se-nav-row">
        {step > 1 ? (
          <button className="se-btn-back" onClick={goBack}>← Back</button>
        ) : (
          <span />
        )}
        {step < 6 ? (
          <button className="se-btn-next" onClick={goNext}>Continue →</button>
        ) : (
          <button className="se-btn-next" onClick={() => alert("Coming Soon!")}>Get quotes near me ↗</button>
        )}
      </div>
    </div>
  );
}